import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { format, addDays } from 'date-fns';
import { ru, enUS, et, ka, hy, az, it, hi } from 'date-fns/locale';
import '../styles/Checkout.css';

const Checkout = () => {
  const { cart, clearCart, getCartTotal } = useCart();
  const { user, loading: userLoading, loadUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getDateLocale = () => {
    const locales = { ru, en: enUS, et, ka, hy, az, it, hi };
    return locales[i18n.language] || et;
  };

  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const deliveryRules = user?.deliveryRules;
  const allowedDays = deliveryRules?.days || [];

  // При монтировании компонента перезагружаем данные пользователя
  useEffect(() => {
    const reloadUserData = async () => {
      if (!userLoading && user) {
        try {
          await loadUser();
        } catch (error) {
          console.error('Ошибка загрузки данных пользователя:', error);
        } finally {
          setInitialLoadDone(true);
        }
      } else if (!userLoading) {
        setInitialLoadDone(true);
      }
    };
    
    reloadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Если данные пользователя ещё загружаются, показываем загрузку
  if (userLoading || !initialLoadDone) {
    return (
      <div className="loading-checkout">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  // Если пользователь не авторизован
  if (!user) {
    return (
      <div className="empty-checkout">
        <h2>{t('unauthorized')}</h2>
        <p>{t('loginTitle')}</p>
        <button onClick={() => navigate('/login')} className="btn-primary">
          {t('loginButton')}
        </button>
      </div>
    );
  }

  // Если у пользователя нет правил доставки
  if (!deliveryRules) {
    return (
      <div className="empty-checkout">
        <h2>{t('deliveryRulesNotConfigured', 'Delivery rules not configured')}</h2>
        <p>{t('contactAdminForDeliveryRules', 'Please contact the administrator to configure delivery rules for your account.')}</p>
        <button onClick={() => navigate('/catalog')} className="btn-primary">
          {t('goToCatalog')}
        </button>
      </div>
    );
  }

  // Получить доступные даты (следующие 30 дней с учетом разрешенных дней недели)
  const getAvailableDates = () => {
    const dates = [];
    const dayMap = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 0
    };

    // If no allowed days configured, return empty array
    if (!allowedDays || allowedDays.length === 0) {
      return dates;
    }

    const now = new Date();

    for (let i = 0; i <= 30; i++) {
      const date = addDays(now, i);
      const dayOfWeek = date.getDay();
      
      // Проверяем, разрешен ли этот день недели
      const isAllowed = allowedDays.some(day => dayMap[day] === dayOfWeek);
      
      if (isAllowed) {
        dates.push(date);
      }
    }

    return dates;
  };

  const availableDates = getAvailableDates();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!deliveryDate) {
      setError(t('selectDeliveryDate', 'Please select a delivery date'));
      return;
    }

    if (cart.length === 0) {
      setError(t('cartEmpty'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Parse the date string (yyyy-MM-dd format) and create a Date object
      const [year, month, day] = deliveryDate.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);
      
      const orderData = {
        items: cart.map(item => ({
          product: item.product._id,
          quantity: item.quantity,
          unit: item.unit
        })),
        deliveryDate: selectedDate,
        notes
      };
      

      await ordersAPI.createOrder(orderData);
      
      clearCart();
      navigate('/orders', { 
        state: { message: t('orderPlacedSuccess', 'Order successfully created and sent for review') } 
      });
    } catch (error) {
      console.error('Ошибка создания заказа:', error);
      setError(error.response?.data?.message || t('orderCreationFailed', 'Failed to create order'));
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="empty-checkout">
        <h2>{t('cartEmpty')}</h2>
        <button onClick={() => navigate('/catalog')} className="btn-primary">
          {t('goToCatalog')}
        </button>
      </div>
    );
  }

  const total = getCartTotal();

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1>{t('checkoutTitle')}</h1>

        <div className="checkout-content">
          <div className="order-summary">
            <h2>{t('yourOrder', 'Your order')}</h2>
            <div className="order-items">
              {cart.map((item) => {
                let price;
                if (item.unit === 'box') {
                  price = item.product.finalBoxPrice || item.product.boxPrice || item.product.price * (item.product.boxQuantity || 1);
                } else {
                  price = item.product.finalPrice || item.product.price;
                }
                const itemTotal = price * item.quantity;
                const unitLabel = item.unit === 'box' ? `${t('boxUnit')} (${item.product.boxQuantity} ${t('pieces')})` : t('pieces');
                
                return (
                  <div key={`${item.product._id}-${item.unit}`} className="order-item">
                    <div className="order-item-info">
                      <span className="item-name">{item.product.name}</span>
                      <span className="item-quantity">
                        {item.quantity} {unitLabel} × {price.toFixed(2)} €
                      </span>
                    </div>
                    <span className="item-total">{itemTotal.toFixed(2)} €</span>
                  </div>
                );
              })}
            </div>
            <div className="order-total">
              <span>{t('total')}</span>
              <span className="total-amount">{total.toFixed(2)} €</span>
            </div>
            <div className="price-disclaimer" style={{ marginTop: '10px', padding: '10px'}}>
            <small
            dangerouslySetInnerHTML={{
              __html: t(
                'priceDisclaimer',
                'Конечная цена может отличаться в зависимости от комплектации.<br/>К сумме добавляется НДС.'
              )
            }}
          />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="checkout-form">
            <h2>{t('deliveryDetails', 'Delivery details')}</h2>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <div className="form-group">
              <label htmlFor="deliveryDate">{t('deliveryDateLabel')} *</label>
              <select
                id="deliveryDate"
                value={deliveryDate}
                onChange={(e) => {
                  setDeliveryDate(e.target.value);
                }}
                required
                disabled={loading}
              >
                <option value="">{t('selectDate', 'Select date')}</option>
                {availableDates.map((date, index) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  return (
                    <option key={index} value={dateStr}>
                      {format(date, 'EEEE, d MMMM yyyy', { locale: getDateLocale() })}
                    </option>
                  );
                })}
              </select>
              {availableDates.length === 0 && (
                <small className="form-hint error">
                  {t('noAvailableDates', 'No available delivery dates. Contact administrator.')}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="notes">{t('additionalInfo', 'Additional information')}</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('orderComments', 'Order comments...')}
                rows="4"
                disabled={loading}
              />
            </div>

            <div className="delivery-info">
              <h3>{t('deliveryAddress')}</h3>
              <p>
                {user?.address?.street}<br />
                {user?.address?.postalCode} {user?.address?.city}<br />
                {user?.address?.country}
              </p>
              <p className="contact-info">
                <strong>{t('contactPerson')}:</strong><br />
                {user?.contactPerson?.firstName} {user?.contactPerson?.lastName}<br />
                {user?.contactPerson?.phone}
              </p>
            </div>

            <button 
              type="submit" 
              className="btn-submit-order"
              disabled={loading || availableDates.length === 0}
            >
              {loading ? t('submitting', 'Submitting...') : t('submitOrder', 'Submit order')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

